import { createReducer, on } from '@ngrx/store';
import { Departures } from 'app/core/enums/common.enum';
import * as TotalGrossActions from '../total-gross/total-gross.actions';
import * as OtherSalesActions from './other-sales.actions';
import { OtherSale } from './other-sales.models';

// ----------------------------------------------------------------------
// @ States
// ----------------------------------------------------------------------

export interface OtherSalesState {
    sales: OtherSale[];
    sale: OtherSale | null;
    commissions: { commissions: any[]; totals: any };
    isLoading: boolean;
    isLoadingCommissions: boolean;
    error: string | null;
}

// ----------------------------------------------------------------------
// @ Initial States
// ----------------------------------------------------------------------

export const initialOtherSalesState: OtherSalesState = {
    sales: [],
    sale: null,
    commissions: { commissions: [], totals: {} },
    isLoading: false,
    isLoadingCommissions: false,
    error: null,
};

// ----------------------------------------------------------------------
// @ Reducers
// ----------------------------------------------------------------------

export const otherSalesReducer = createReducer(
    initialOtherSalesState,

    // On Load Other Sales
    on(OtherSalesActions.loadOtherSales, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(OtherSalesActions.loadOtherSalesSuccess, (state, { data }) => ({
        ...state,
        isLoading: false,
        sales: data,
    })),
    on(OtherSalesActions.loadOtherSalesFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Clear
    on(OtherSalesActions.clearOtherSales, (state) => ({
        ...state,
        sales: [],
        sale: null,
        commissions: { commissions: [], totals: {} },
        isLoading: false,
        isLoadingCommissions: false,
        error: null,
    })),

    // On Load Single Other Sale
    on(OtherSalesActions.loadOtherSale, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(OtherSalesActions.loadOtherSaleSuccess, (state, { data }) => ({
        ...state,
        isLoading: false,
        sale: data,
    })),
    on(OtherSalesActions.loadOtherSaleFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Create
    on(OtherSalesActions.createOtherSale, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(OtherSalesActions.createOtherSaleSuccess, (state, { sale }) => ({
        ...state,
        isLoading: false,
        sales: ((): OtherSale[] => {
            const sales = state.sales || [];
            let updateSales = [];
            sales.some((s) => s.location.id === sale.location.id)
                ? (updateSales = sales.map((_sale: OtherSale) =>
                      _sale.location.id === sale.location.id
                          ? {
                                ..._sale,
                                children: [..._sale.children, ...sale.children],
                                totals: sale.totals,
                            }
                          : _sale
                  ))
                : (updateSales = [...sales, sale]);
            return updateSales;
        })(),
    })),
    on(OtherSalesActions.createOtherSaleFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Update
    on(OtherSalesActions.updateOtherSale, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(OtherSalesActions.updateOtherSaleSuccess, (state, { data }) => ({
        ...state,
        isLoading: false,
        sales: state.sales.map((s) =>
            s.location.id === data.location.id
                ? {
                      ...s,
                      children: s.children.map((child) =>
                          child.saleId === data.children[0].saleId
                              ? data.children[0]
                              : child
                      ),
                      totals: data.totals,
                  }
                : s
        ),
    })),
    on(OtherSalesActions.updateOtherSaleFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Update Location Gross
    on(
        TotalGrossActions.updateTotalGrossSuccess,
        (state, { data, departure }) => ({
            ...state,
            isLoading: false,
            sales: state.sales.map((s) =>
                s.location.id === data.location_id &&
                departure === Departures.OTHER
                    ? {
                          ...s,
                          cash: data.cash,
                          credit_card: data.credit_card,
                          total_money: data.total_gross,
                      }
                    : s
            ),
        })
    ),

    // On Delete
    on(OtherSalesActions.deleteOtherSale, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        OtherSalesActions.deleteOtherSaleSuccess,
        (state, { deletedSales, remainingSales }) => ({
            ...state,
            isLoading: false,
            sales: (() => {
                if (remainingSales.length === 0) {
                    return state.sales.filter(
                        (sales: OtherSale) =>
                            !sales.children.some(
                                (child) => child.saleId === deletedSales[0]
                            )
                    );
                } else {
                    console.warn(
                        'Remaining sales after deletion:',
                        remainingSales
                    );
                }
            })(),
        })
    ),
    on(OtherSalesActions.deleteOtherSaleFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Delete Sale Item
    on(OtherSalesActions.deleteOtherSaleItem, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        OtherSalesActions.deleteOtherSaleItemSuccess,
        (state, { deletedSaleItem, totals }) => ({
            ...state,
            isLoading: false,
            sales: state.sales.map((_sale: OtherSale) =>
                _sale.children.some((child) => child.saleId === deletedSaleItem)
                    ? {
                          ..._sale,
                          children: _sale.children.filter(
                              (child) => child.saleId !== deletedSaleItem
                          ),
                          totals: totals,
                      }
                    : _sale
            ),
        })
    ),
    on(OtherSalesActions.deleteOtherSaleItemFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    }))
);
